import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useModalOutsideClick from '../../hooks/useModalOutsideClick';
import useSortedData from '../../hooks/useSortedData';
import ViewEntrantsModal from './ViewEntrantsModal';
import ViewEntryModal from './ViewEntryModal';
import './ViewOwnershipModal.css';

const formatScore = (score) => {
  if (score === null || score === undefined) return '—';
  if (score === 0) return 'E';
  return score > 0 ? `+${score}` : score.toString();
};

const formatChips = (chips) => {
  if (chips === null || chips === undefined) return '—';
  return chips.toLocaleString();
};

const ViewOwnershipModal = ({
  show,
  onClose,
  players,
  entrants,
  currentUsername,
  contest,
  userPlayerNames
}) => {
  // Add the useModalOutsideClick hook to close when clicking outside
  useModalOutsideClick(show, onClose, "view-ownership-overlay");

  const isPoker = contest.sport === 'Poker';

  // State for showing entrants modal and selected player
  const [showEntrantsModal, setShowEntrantsModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  
  // State for entry modal
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showViewEntryModal, setShowViewEntryModal] = useState(false);

  // Memoize the raw ownership data, separating players with ownership from the "All Others" group.
  const { playersWithOwnership, allOthersRow } = useMemo(() => {
    if (!players || !entrants || entrants.length === 0) {
      return { playersWithOwnership: [], allOthersRow: [] };
    }

    const totalEntrants = entrants.length;
    const playerOwnership = {};
    const playerMetricMap = new Map();

    // Initialize ownership counts and map scores for all players
    players.forEach(player => {
      playerOwnership[player.name] = 0;
      const metric = isPoker 
        ? player.sportSpecificData?.currentChipCount 
        : player.sportSpecificData?.score;
      playerMetricMap.set(player.name, metric);
    });

    // Count how many teams each player is on
    entrants.forEach(entrant => {
      if (entrant.team) {
        if (entrant.team.captain) playerOwnership[entrant.team.captain]++;
        entrant.team.flexPlayers?.forEach(player => {
          playerOwnership[player]++;
        });
      }
    });

    // Convert to an array of objects
    const ownershipArray = Object.entries(playerOwnership)
      .map(([name, count]) => ({
        id: name,
        player: name,
        percentage: Math.round((count / totalEntrants) * 100),
        count: count,
        metric: playerMetricMap.get(name)
      }));
      
    // Separate players that have been drafted from those that haven't
    const withOwnership = ownershipArray.filter(p => p.percentage > 0);
    const withoutOwnership = ownershipArray.filter(p => p.percentage === 0);

    // Create a single "All others" row if there are undrafted players
    const othersRow = withoutOwnership.length > 0
      ? [{ id: 'others', player: 'All others', percentage: 0, count: 0, metric: null }]
      : [];

    return { playersWithOwnership: withOwnership, allOthersRow: othersRow };
  }, [players, entrants, isPoker]);

  // Apply the sorting hook to the players who have ownership
  // For golf contests, sort initially by percentage; otherwise by metric
  const initialSortKey = contest.sport === 'Golf' ? 'percentage' : 'metric';
  const { 
    sortedData, 
    requestSort, 
    sortConfig 
  } = useSortedData(
    playersWithOwnership,
    initialSortKey // Initial sort key
  );

  // Re-combine the sorted data with the "All Others" row for the final display
  const finalOwnershipData = useMemo(() => [
    ...sortedData, 
    ...allOthersRow
  ], [sortedData, allOthersRow]);
  
  // Helper to display sort indicators in the table header
  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  const handleCountClick = (player) => {
    if (player === 'others') return; // Don't show modal for "All others"
    setSelectedPlayer(player);
    setShowEntrantsModal(true);
  };

  const handleEntryClick = (entry) => {
    if (!entry || !contest) return;
    setSelectedEntry(entry);
    setShowViewEntryModal(true);
  };

  const handleCloseEntryModal = () => {
    setShowViewEntryModal(false);
    setSelectedEntry(null);
  };

  if (!show) return null;

  // Add defensive check for contest data
  const hasRequiredContestData = contest && 
    contest.tournamentName && 
    contest.entryFee !== undefined && 
    contest.adminFee !== undefined && 
    contest.salaryCap !== undefined && 
    contest.status && 
    contest.sport && 
    Array.isArray(contest.players);

  return (
    <div className="view-ownership-overlay">
      <div className="view-ownership-dialog">
        <div className="view-ownership-header">
          <h3>Player Ownership Percentages</h3>
          <button className="view-ownership-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="view-ownership-content">
          {finalOwnershipData.length > 0 ? (
            <table className="ownership-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('player')}>PLAYER{getSortIndicator('player')}</th>
                  <th onClick={() => requestSort('percentage')}>%{getSortIndicator('percentage')}</th>
                  <th onClick={() => requestSort('count')}>#{getSortIndicator('count')}</th>
                  <th onClick={() => requestSort('metric')}>{isPoker ? 'CHIPS' : 'SCORE'}{getSortIndicator('metric')}</th>
                </tr>
              </thead>
              <tbody>
                {finalOwnershipData.map((row) => (
                  <tr 
                    key={row.id}
                    onClick={() => handleCountClick(row.id)}
                    style={{ cursor: row.id !== 'others' ? 'pointer' : 'default' }}
                    className={row.id !== 'others' ? 'clickable-row' : ''}
                  >
                    <td className="player">
                      {userPlayerNames.includes(row.player.toLowerCase()) && '⭐ '}
                      {row.player}
                    </td>
                    <td className="percentage">{row.percentage}%</td>
                    <td className="count">
                      {row.count}/{entrants.length}
                    </td>
                    <td className="score">{isPoker ? formatChips(row.metric) : formatScore(row.metric)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-ownership-message">No ownership data available.</p>
          )}
        </div>
      </div>
      {showEntrantsModal && (
        <ViewEntrantsModal
          show={showEntrantsModal}
          onClose={() => setShowEntrantsModal(false)}
          entrants={entrants.filter(entrant => {
            if (!entrant.team) return false;
            return entrant.team.captain === selectedPlayer || 
                   entrant.team.flexPlayers?.includes(selectedPlayer);
          })}
          players={players}
          currentUsername={currentUsername}
          onEntryClick={handleEntryClick}
          contestStatus={contest.status}
          customTitle={selectedPlayer ? `Teams with ${selectedPlayer}` : null}
        />
      )}
      {hasRequiredContestData && (
        <ViewEntryModal
          show={showViewEntryModal}
          onClose={handleCloseEntryModal}
          onWithdraw={() => {}} // No need for withdraw functionality here
          entry={selectedEntry}
          contest={contest}
          isWithdrawing={false}
          isSuccess={false}
          userPlayers={userPlayerNames}
        />
      )}
    </div>
  );
};

ViewOwnershipModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  entrants: PropTypes.arrayOf(
    PropTypes.shape({
      team: PropTypes.shape({
        captain: PropTypes.string,
        flexPlayers: PropTypes.arrayOf(PropTypes.string)
      })
    })
  ).isRequired,
  currentUsername: PropTypes.string,
  contest: PropTypes.shape({
    tournamentName: PropTypes.string.isRequired,
    entryFee: PropTypes.number.isRequired,
    adminFee: PropTypes.number.isRequired,
    salaryCap: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      salary: PropTypes.number,
      finish: PropTypes.number,
      cashedFor: PropTypes.number,
      sportSpecificData: PropTypes.shape({
        score: PropTypes.number,
        rating: PropTypes.number,
        kd: PropTypes.string,
        status: PropTypes.string,
        thru: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      })
    })).isRequired
  }).isRequired,
  userPlayerNames: PropTypes.arrayOf(PropTypes.string)
};

ViewOwnershipModal.defaultProps = {
  userPlayerNames: []
};

export default ViewOwnershipModal; 